import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Heart, ChevronRight, ChevronLeft, Gift, 
  Home, Smartphone, Shirt, Sparkles, Baby, Palette, Search, 
  Gamepad2, BookOpen, Star, Package, Utensils, Briefcase, 
  Dumbbell, ShoppingBag, X, 
  // Sparkle, ArrowRight, 
  Filter } from 'lucide-react';
import Header from '../components/Header';
import MainFooter from '../components/MainFooter';
import API_URL from '../config/api';

const CATEGORY_ICONS = {
  'All': Gift,
  'Home & Living': Home,
  'Tech & Electronics': Smartphone,
  'Fashion & Jewelry': Shirt,
  'Health & Beauty': Sparkles,
  'Toys & Games': Gamepad2,
  'Kids & Baby': Baby,
  'Arts & Crafts': Palette,
  'Books & Media': BookOpen,
  'Special Occasions': Star,
  'Food & Snacks': Utensils,
  'Kitchen & Dining': Package,
  'Office & Stationery': Briefcase,
  'Outdoor & Sports': Dumbbell,
  'Other': ShoppingBag,
};


// MOVE ProductCard OUTSIDE HomeScreen component
const ProductCard = React.memo(({ product, isInWishlist, onWishlistToggle, onProductClick }) => {
  return (
    <div 
      onClick={onProductClick}
      className="group relative bg-white rounded-xl sm:rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full cursor-pointer border border-slate-100 active:scale-95 sm:hover:-translate-y-1"
    >
      <div className="relative overflow-hidden aspect-square bg-slate-50 flex-shrink-0">
        {/* Skeleton that disappears when image loads - no React state needed */}
        <div className="absolute inset-0 bg-slate-100 product-skeleton" />
        
        <img 
          src={product.image_urls?.[0]}
          alt={product.title} 
          className="w-full h-full object-contain p-2 sm:p-4 transition-all duration-500 hover:scale-105 opacity-0"
          loading="lazy"
          onLoad={(e) => {
            e.target.style.opacity = '1';
            e.target.style.transition = 'opacity 0.3s ease-in';
            // Hide skeleton via CSS sibling selector
            const skeleton = e.target.previousSibling;
            if (skeleton) skeleton.style.opacity = '0';
          }}
        />
        
        <button
          onClick={onWishlistToggle}
          type="button"
          className={`absolute top-1 right-1 w-11 h-6 sm:w-9 sm:h-9 flex items-center justify-center rounded-full shadow-md transition-all duration-200 active:scale-90 ${isInWishlist ? 'bg-red-50 text-red-500' : 'bg-white/90 backdrop-blur-sm text-slate-400'}`}
        >
          <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isInWishlist ? 'fill-red-500' : ''}`} />
        </button>

        {product.savings_percentage && (
          <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md shadow-sm">
            -{product.savings_percentage}%
          </div>
        )}
      </div>

      <div className="p-2.5 sm:p-4 flex flex-col flex-grow">
        <span className="text-[9px] sm:text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-0.5 sm:mb-1">
          {product.main_category || 'Gift'}
        </span>
        
        <h3 className="font-semibold text-slate-800 text-xs sm:text-sm line-clamp-2 mb-1 sm:mb-2 leading-snug">
          {product.title}
        </h3>
        
        <div className="mt-auto pt-1.5 sm:pt-2 border-t border-slate-50">
          <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
            <span className="text-sm sm:text-lg font-bold text-[#06402B]">
              {product.price_display}
            </span>
            {product.list_price && product.list_price !== product.price && (
              <span className="text-[10px] sm:text-xs text-slate-400 line-through">
                ${Number(product.list_price).toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// DISPLAY NAME FOR DEBUGGING
ProductCard.displayName = 'ProductCard';


const HomeScreen = ({ 
  user, 
  onLogout, 
  onProductSelect,
  products = [], 
  productsLoading = false, 
  currentScreen, 
  onNavigate, 
  wishlist = [], 
  toggleWishlist,
  showLoginModal,
  setShowLoginModal
}) => {
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [valentinesProducts, setValentinesProducts] = useState([]);
  const [valentinesLoading, setValentinesLoading] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const [forHimProducts, setForHimProducts] = useState([]);
  const [forHerProducts, setForHerProducts] = useState([]);
  const [forKidsProducts, setForKidsProducts] = useState([]);
  
  const chipScrollRef = useRef(null);
  const scrollRef = useRef(null);
  const mainRef = useRef(null);

  // Handle scroll detection for sticky headers
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = useMemo(() => {
    const categoryCounts = {};
    products.forEach(product => {
      const mainCat = product.main_category || 'Other';
      categoryCounts[mainCat] = (categoryCounts[mainCat] || 0) + 1;
    });

    const categoriesArray = Object.entries(categoryCounts)
      .map(([name, count]) => ({
        name,
        count,
        icon: CATEGORY_ICONS[name] || Gift
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return [
      { name: 'All', count: products.length, icon: Gift },
      ...categoriesArray
    ];
  }, [products]);

  useEffect(() => {
    const fetchValentinesProducts = async () => {
      setValentinesLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/filter/occasion/valentine's day?limit=20`);
        if (!response.ok) throw new Error('Failed to fetch');
        const data = await response.json();
        setValentinesProducts(data.products || []);
      } catch (error) {
        console.error('Error:', error);
        setValentinesProducts([]);
      } finally {
        setValentinesLoading(false);
      }
    };

    if (products.length > 0) fetchValentinesProducts();
  }, [products.length]);

  useEffect(() => {
    if (products.length > 0) {
      const himProducts = products.filter(p => {
        const title = (p.title || '').toLowerCase();
        if (title.includes('women') || title.includes('woman') || title.includes('girl')) return false;
        const malePattern = /\b(men|man|boy|male|mens|him|husband|boyfriend|brother|dad|father|son)\b/;
        return malePattern.test(title);
      });
      setForHimProducts(himProducts);

      const herProducts = products.filter(p => {
        const title = (p.title || '').toLowerCase();
        if (title.includes(' men ') || title.includes(' man ') || title.includes(' boy ')) return false;
        const femalePattern = /\b(women|woman|girl|female|womens|her|wife|girlfriend|sister|mom|mother|daughter|ladies|lady)\b/;
        return femalePattern.test(title);
      });
      setForHerProducts(herProducts);

      const kidsProducts = products.filter(p => {
        const category = (p.main_category || '').toLowerCase();
        const title = (p.title || '').toLowerCase();
        const isKidsCategory = category.includes('kids') || category.includes('baby');
        const kidPattern = /\b(kid|kids|child|children|baby|infant|toddler)\b/;
        return isKidsCategory || kidPattern.test(title);
      });
      setForKidsProducts(kidsProducts);
    }
  }, [products]);

  // const genderCollections = useMemo(() => [
  //   { 
  //     title: "For Him", 
  //     subtitle: `${forHimProducts.length} gifts`, 
  //     color: "from-slate-700 via-slate-800 to-slate-900", 
  //     image: "https://images.unsplash.com/photo-1516826957135-700dedea698c?w=800&q=80",
  //     products: forHimProducts,
  //     icon: Shirt
  //   },
  //   { 
  //     title: "For Her", 
  //     subtitle: `${forHerProducts.length} gifts`, 
  //     color: "from-rose-500 via-pink-600 to-rose-700", 
  //     image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=800&q=80",
  //     products: forHerProducts,
  //     icon: Heart
  //   },
  //   { 
  //     title: "For Kids", 
  //     subtitle: `${forKidsProducts.length} gifts`, 
  //     color: "from-violet-500 via-purple-600 to-indigo-700", 
  //     image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80",
  //     products: forKidsProducts,
  //     icon: Baby
  //   }
  // ], [forHimProducts, forHerProducts, forKidsProducts]);

  const handleSearch = useCallback((query, results) => {
    if (!query && results.length === 0) {
      setSearchActive(false);
      setSearchQuery('');
      setSearchResults([]);
    } else if (!query && results.length > 0) {
      setSearchActive(true);
      setSearchQuery('');
      setSearchResults(results);
    } else {
      setSearchActive(true);
      setSearchQuery(query);
      setSearchResults(results);
    }
    setSelectedCategory('All');
    setVisibleCount(24);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchActive(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (searchActive) return;
    
    if (selectedCategory === 'All') {
      setFilteredProducts(products);
    } else if (selectedCategory === 'For Him') {
      setFilteredProducts(forHimProducts);
    } else if (selectedCategory === 'For Her') {
      setFilteredProducts(forHerProducts);
    } else if (selectedCategory === 'For Kids') {
      setFilteredProducts(forKidsProducts);
    } else {
      const filtered = products.filter(p => p.main_category === selectedCategory);
      setFilteredProducts(filtered);
    }
  }, [products, selectedCategory, searchActive, forHimProducts, forHerProducts, forKidsProducts]);

  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);
    clearSearch();
    setVisibleCount(24);
    setShowMobileFilters(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    setSelectedCategory('All');
    clearSearch();
    setVisibleCount(24);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // const handleViewCollection = (collection) => {
  //   setSelectedCategory(collection.title);
  //   clearSearch();
  //   setVisibleCount(24);
  //   window.scrollTo({ top: 0, behavior: 'smooth' });
  // };

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setTimeout(() => { 
      setVisibleCount(prev => prev + 12); 
      setLoadingMore(false); 
    }, 600);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = container.offsetWidth * 0.85;
      const start = container.scrollLeft;
      const target = direction === 'left' ? start - scrollAmount : start + scrollAmount;
      const duration = 600;
      const startTime = performance.now();
      
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      
      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        container.scrollLeft = start + (target - start) * easedProgress;
        if (progress < 1) requestAnimationFrame(animateScroll);
      };
      
      requestAnimationFrame(animateScroll);
    }
  };

  const displayProducts = searchActive ? searchResults : filteredProducts;
  const hasMore = visibleCount < displayProducts.length;
  const isLandingView = selectedCategory === 'All' && !searchActive;

  // Mobile-optimized Login Modal
  const LoginModal = useCallback(() => {
    if (!showLoginModal) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
        <div 
          className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => setShowLoginModal(false)}
        />
        
        <div className="relative bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl p-6 sm:p-8 animate-in slide-in-from-bottom-10 sm:zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
          <div className="sm:hidden w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-6 mt-2" />
          
          <button
            onClick={() => setShowLoginModal(false)}
            type="button"
            className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>

          {/* <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-[#06402B] to-emerald-700 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-emerald-200">
            <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-white fill-white/20" />
          </div> */}

          <h3 className="text-xl sm:text-2xl sm:text-3xl font-bold text-slate-800 text-center mb-2">
            Join GivAura
          </h3>
          <p className="text-slate-500 text-center mb-6 sm:mb-8 text-sm sm:text-base">
            Create a free account to unlock personalized recommendations.
          </p>

          <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
            {[
              'Save unlimited products to wishlist',
              'AI-powered gift recommendations',
              'Exclusive deals and early access',
              'Sync across all your devices'
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 text-xs sm:text-sm text-slate-700">
                <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-[#06402B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{feature}</span>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <button
              onClick={() => { setShowLoginModal(false); onNavigate('register'); }}
              type="button"
              className="w-full px-6 py-3.5 sm:py-4 bg-[#06402B] text-white rounded-xl hover:bg-[#053527] transition-all font-semibold text-sm sm:text-base shadow-lg shadow-emerald-200 hover:shadow-xl active:scale-95"
            >
              Create Free Account
            </button>
            
            <button
              onClick={() => { setShowLoginModal(false); onNavigate('login'); }}
              type="button"
              className="w-full px-6 py-3.5 sm:py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-xl hover:border-[#06402B] hover:text-[#06402B] transition-all font-semibold text-sm sm:text-base active:scale-95"
            >
              Sign In
            </button>
          </div>

          <button
            onClick={() => setShowLoginModal(false)}
            type="button"
            className="w-full mt-4 sm:mt-6 text-sm text-slate-400 hover:text-slate-600 transition-colors font-medium py-2"
          >
            Continue as guest
          </button>
        </div>
      </div>
    );
  }, [showLoginModal, setShowLoginModal, onNavigate]);

  // Enhanced Product Card - Mobile Optimized
  // const ProductCard = React.memo(({ product, isInWishlist, onWishlistToggle, onProductClick }) => {
  //   const [imageLoaded, setImageLoaded] = useState(false);
    
  //   return (
  //     <div 
  //       onClick={onProductClick}
  //       className="group relative bg-white rounded-xl sm:rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col h-full cursor-pointer border border-slate-100 active:scale-95 sm:hover:-translate-y-1"
  //     >
  //       <div className="relative overflow-hidden aspect-square bg-slate-50 flex-shrink-0">
  //         {!imageLoaded && (
  //           <div className="absolute inset-0 bg-slate-100 animate-pulse" />
  //         )}
  //         <img 
  //           src={product.image_urls?.[0]}
  //           alt={product.title} 
  //           className={`w-full h-full object-contain p-2 sm:p-4 transition-all duration-500 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
  //           loading="lazy"
  //           onLoad={() => setImageLoaded(true)}
  //         />
          
  //         <button
  //           onClick={onWishlistToggle}
  //           type="button"
  //           className={`absolute top-1 right-1 w-11 h-6 sm:w-9 sm:h-9 flex items-center justify-center rounded-full shadow-md transition-all duration-200 active:scale-90 ${isInWishlist ? 'bg-red-50 text-red-500' : 'bg-white/90 backdrop-blur-sm text-slate-400'}`}
  //         >
  //           <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isInWishlist ? 'fill-red-500' : ''}`} />
  //         </button>

  //         {product.savings_percentage && (
  //           <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md shadow-sm">
  //             -{product.savings_percentage}%
  //           </div>
  //         )}
  //       </div>

  //       <div className="p-2.5 sm:p-4 flex flex-col flex-grow">
  //         <span className="text-[9px] sm:text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-0.5 sm:mb-1">
  //           {product.main_category || 'Gift'}
  //         </span>
          
  //         <h3 className="font-semibold text-slate-800 text-xs sm:text-sm line-clamp-2 mb-1 sm:mb-2 leading-snug">
  //           {product.title}
  //         </h3>
          
  //         <div className="mt-auto pt-1.5 sm:pt-2 border-t border-slate-50">
  //           <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
  //             <span className="text-sm sm:text-lg font-bold text-[#06402B]">
  //               {product.price_display}
  //             </span>
  //             {product.list_price && product.list_price !== product.price && (
  //               <span className="text-[10px] sm:text-xs text-slate-400 line-through">
  //                 ${Number(product.list_price).toFixed(2)}
  //               </span>
  //             )}
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // });

  const ProductSkeleton = () => (
    <div className="bg-white rounded-xl sm:rounded-2xl border border-slate-100 overflow-hidden">
      <div className="aspect-square bg-slate-100 animate-pulse" />
      <div className="p-2.5 sm:p-4 space-y-2 sm:space-y-3">
        <div className="h-2 sm:h-3 bg-slate-100 rounded w-1/3 animate-pulse" />
        <div className="h-3 sm:h-4 bg-slate-100 rounded w-full animate-pulse" />
        <div className="h-3 sm:h-4 bg-slate-100 rounded w-2/3 animate-pulse" />
        <div className="pt-1.5 sm:pt-2 border-t border-slate-50">
          <div className="h-4 sm:h-5 bg-slate-100 rounded w-1/3 animate-pulse" />
        </div>
      </div>
    </div>
  );

  const handleProductClick = useCallback((product) => {
    onProductSelect(product);
  }, [onProductSelect]);

  const handleWishlistToggle = useCallback((asin, e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(asin);
  }, [toggleWishlist]);

  const visibleProducts = useMemo(() => {
    return displayProducts.slice(0, visibleCount);
  }, [displayProducts, visibleCount]);

  // Mobile Filter Drawer
  const MobileFilterDrawer = () => {
    if (!showMobileFilters) return null;
    
    return (
      <div className="fixed inset-0 z-50 lg:hidden">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
        <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl max-h-[80vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom-10 duration-300">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-lg text-slate-800">Categories</h3>
            <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-slate-100 rounded-full">
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
          <div className="overflow-y-auto p-4 space-y-1">
            {categories.map((cat) => {
              const IconComponent = cat.icon;
              const isActive = selectedCategory === cat.name;
              return (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat.name)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${isActive ? 'bg-[#06402B] text-white shadow-md' : 'hover:bg-slate-50 text-slate-700'}`}
                >
                  <IconComponent className={`w-5 h-5 ${isActive ? 'text-white' : 'text-[#06402B]'}`} />
                  <span className="font-medium flex-1 text-left">{cat.name}</span>
                  {/* <span className={`text-xs ${isActive ? 'text-emerald-100' : 'text-slate-400'}`}>{cat.count}</span> */}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FBFCF6] pb-safe" ref={mainRef}>
      <Header 
        user={user} 
        onLogout={onLogout} 
        currentScreen={currentScreen} 
        onNavigate={onNavigate}
        onSearch={handleSearch}
      />

      <div className="max-w-[1920px] mx-auto">
        <div className="flex">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block lg:w-72 xl:w-80 lg:flex-shrink-0 lg:sticky lg:top-20 lg:self-start lg:h-[calc(100vh-5rem)] lg:overflow-y-auto lg:border-r lg:border-slate-200/80 lg:bg-white/50 lg:backdrop-blur-xl scrollbar-hide">
            <div className="p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                    {/* <Sparkles className="w-5 h-5 text-[#06402B]" /> */}
                    Categories
                  </h3>
                  {(selectedCategory !== 'All' || searchActive) && (
                    <button onClick={handleBackToHome} className="text-xs font-semibold text-emerald-700 hover:underline">
                      Reset
                    </button>
                  )}
                </div>
                <div className="h-px bg-gradient-to-r from-slate-200 to-transparent" />
              </div>

              <div className="space-y-1.5">
                {categories.map((cat) => {
                  const IconComponent = cat.icon;
                  const isActive = selectedCategory === cat.name;
                  return (
                    <button 
                      key={cat.name} 
                      onClick={() => handleCategorySelect(cat.name)} 
                      className={`w-full group flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 text-left relative overflow-hidden ${isActive ? 'bg-[#06402B] text-white shadow-lg shadow-emerald-900/20' : 'hover:bg-white hover:shadow-md text-slate-600'}`}
                    >
                      {isActive && <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-[#06402B]" />}
                      <div className={`relative z-10 p-2 rounded-lg ${isActive ? 'bg-white/20' : 'bg-slate-100 group-hover:bg-emerald-50'}`}>
                        <IconComponent className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#06402B]'}`} />
                      </div>
                      <div className="relative z-10 flex-1 min-w-0">
                        <span className="font-medium text-sm block truncate">{cat.name}</span>
                        {/* <span className={`text-xs ${isActive ? 'text-emerald-100' : 'text-slate-400'}`}>{cat.count} items</span> */}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            {/* Mobile Category Chips */}
            <div className={`lg:hidden sticky top-[55px] sm:top-16 z-30 transition-all duration-300 border-b border-slate-200/80 ${isScrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm' : 'bg-[#FBFCF6]'}`}>
              <div className="px-3 sm:px-4 py-2.5 sm:py-3">
                <div className="flex items-center gap-2">
                  <div 
                    ref={chipScrollRef}
                    className="flex-1 flex gap-2 overflow-x-auto scrollbar-hide snap-x"
                  >
                    {categories.slice(0, 6).map((cat) => {
                      const IconComponent = cat.icon;
                      const isActive = selectedCategory === cat.name;
                      return (
                        <button
                          key={cat.name}
                          onClick={() => handleCategorySelect(cat.name)}
                          className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap snap-start border ${isActive ? 'bg-[#06402B] text-white border-[#06402B] shadow-md' : 'bg-white text-slate-600 border-slate-200'}`}
                        >
                          <IconComponent className={`w-3.5 h-3.5 ${isActive ? 'text-emerald-200' : 'text-slate-400'}`} />
                          <span>{cat.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button 
                    onClick={() => setShowMobileFilters(true)}
                    className="flex-shrink-0 p-2.5 bg-white border border-slate-200 rounded-xl text-slate-600 shadow-sm"
                  >
                    <Filter className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
              {isLandingView && (
                <>
                  {/* AI Gift Finder Banner */}
                  <div className="mb-4 sm:mb-6 md:mb-8">
                    <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 p-4 sm:p-6 md:p-12 border border-emerald-200/50 shadow-lg">
                      <div className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-emerald-200/40 rounded-full blur-3xl"></div>
                      <div className="absolute bottom-0 left-0 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-teal-200/40 rounded-full blur-3xl"></div>

                      <div className="relative z-10 text-center max-w-3xl mx-auto">
                        <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full border border-emerald-200 mb-3 sm:mb-4 md:mb-6 shadow-sm">
                          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-[#06402B]" />
                          <span className="text-[10px] sm:text-xs md:text-sm font-semibold text-[#06402B]">AI-Powered Gift Finder</span>
                        </div>
                        
                        <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold text-slate-800 mb-2 sm:mb-3 md:mb-4">
                          Find the Perfect Gift
                        </h2>
                        <p className="text-xs sm:text-sm md:text-lg text-slate-600 mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto px-2">
                          Tell us about the recipient and let our AI discover thoughtful, personalized gift recommendations.
                        </p>
                        
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            onNavigate('aiFinder');
                          }} 
                          type="button"
                          className="px-5 py-2.5 sm:px-6 sm:py-3 md:px-10 md:py-4 bg-[#06402B] text-white rounded-xl hover:shadow-xl transition-all font-semibold text-sm sm:text-base md:text-lg shadow-lg active:scale-95"
                        >
                          Try AI Gift Finder
                        </button>
                      </div>
                    </div>
                  </div>


                  {/* Valentine's Special - Mobile Horizontal Scroll */}
                  {!valentinesLoading && valentinesProducts.length > 0 && (
                    <div className="mb-8 sm:mb-12">
                      <div className="flex items-end justify-between mb-4 sm:mb-6 px-1">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2.5 py-0.5 sm:px-3 sm:py-1 bg-red-100 text-red-700 text-[10px] sm:text-xs font-bold rounded-full uppercase tracking-wider">Limited Time</span>
                          </div>
                          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-800 flex items-center gap-2">
                            <Heart className="w-5 h-5 sm:w-7 sm:h-7 text-red-500 fill-red-500" />
                            Valentine's Collection
                          </h2>
                        </div>
                      </div>
                      
                      <div className="relative group">
                        <button 
                          onClick={() => scroll('left')} 
                          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 lg:w-12 lg:h-12 bg-white shadow-lg rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:scale-110 -translate-x-1/2 border border-slate-100"
                        >
                          <ChevronLeft className="w-5 h-5 lg:w-6 lg:h-6 text-slate-700" />
                        </button>
                        
                        <div 
                          ref={scrollRef} 
                          className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 px-1 scroll-smooth snap-x snap-mandatory scrollbar-hide mx-0 px-3 sm:mx-0 sm:px-0" 
                        >
                          {valentinesProducts.map((p, idx) => (
                            <div key={p.asin} className="flex-shrink-0 w-[160px] sm:w-[240px] md:w-[280px] snap-start">
                              <ProductCard 
                                product={p}
                                isInWishlist={wishlist.includes(p.asin)}
                                onWishlistToggle={(e) => handleWishlistToggle(p.asin, e)}
                                onProductClick={() => handleProductClick(p)}
                              />
                            </div>
                          ))}
                        </div>
                        
                        <button 
                          onClick={() => scroll('right')} 
                          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 lg:w-12 lg:h-12 bg-white shadow-lg rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:scale-110 translate-x-1/2 border border-slate-100"
                        >
                          <ChevronRight className="w-5 h-5 lg:w-6 lg:h-6 text-slate-700" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Gender Collections - Mobile Stack */}
                  {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-8 sm:mb-12">
                    {genderCollections.map((col, idx) => (
                      <div 
                        key={idx} 
                        onClick={() => handleViewCollection(col)}
                        className="group relative rounded-2xl overflow-hidden aspect-[16/9] sm:aspect-[4/3] cursor-pointer shadow-md sm:shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
                      >
                        <img src={col.image} alt={col.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                        <div className={`absolute inset-0 bg-gradient-to-br ${col.color} opacity-80`} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        
                        <div className="absolute inset-0 p-4 sm:p-6 flex flex-col justify-between text-white">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center`}>
                            <col.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                          </div>
                          
                          <div>
                            <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">{col.title}</h3>
                            <p className="text-xs sm:text-sm opacity-90">{col.subtitle}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div> */}

                  {/* All Products Grid - Mobile 2 columns */}
                  <div className="mb-8 sm:mb-12">
                    <div className="flex items-center justify-between mb-4 sm:mb-6 px-1">
                      <div>
                        <h2 className="text-lg sm:text-2xl font-bold text-slate-800">Trending Gifts</h2>
                        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Handpicked favorites this week</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
                      {visibleProducts.map((p, idx) => (
                        <ProductCard 
                          key={p.asin}
                          product={p}
                          isInWishlist={wishlist.includes(p.asin)}
                          onWishlistToggle={(e) => handleWishlistToggle(p.asin, e)}
                          onProductClick={() => handleProductClick(p)}
                        />
                      ))}
                    </div>
                    
                    {visibleCount < products.length && (
                      <div className="flex justify-center mt-8 sm:mt-12">
                        <button 
                          onClick={loadMore} 
                          disabled={loadingMore} 
                          className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-white border-2 border-slate-200 text-slate-700 rounded-xl hover:border-[#06402B] hover:text-[#06402B] font-semibold shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
                        >
                          {loadingMore ? (
                            <><div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />Loading...</>
                          ) : (
                            <>Load More<ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Search/Category Results View */}
              {!isLandingView && (
                <>
                  <div className="mb-4 sm:mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                      <div>
                        <div className="flex items-center gap-2 mb-1 text-xs sm:text-sm text-slate-500">
                          <span>{searchActive ? 'Search Results' : 'Category'}</span>
                          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-slate-300" />
                          <span className="font-semibold text-[#06402B] truncate max-w-[200px] sm:max-w-xs">
                            {searchActive ? (searchQuery || 'Products') : selectedCategory}
                          </span>
                        </div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
                          {searchActive ? `${displayProducts.length} results` : selectedCategory}
                        </h2>
                      </div>
                      
                      <button
                        onClick={handleBackToHome}
                        className="self-start sm:self-auto px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors font-medium text-sm flex items-center gap-2 active:scale-95"
                      >
                        <X className="w-4 h-4" />
                        <span className="hidden sm:inline">Clear {searchActive ? 'Search' : 'Filter'}</span>
                        <span className="sm:hidden">Back</span>
                      </button>
                    </div>
                    <div className="h-px bg-gradient-to-r from-slate-200 via-slate-200 to-transparent" />
                  </div>

                  {productsLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
                      {[...Array(8)].map((_, i) => <ProductSkeleton key={i} />)}
                    </div>
                  ) : displayProducts.length === 0 ? (
                    <div className="text-center py-12 sm:py-20 bg-white rounded-2xl sm:rounded-3xl border border-slate-100 shadow-sm px-4">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                        <Search className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-2">
                        {searchActive ? 'No matches found' : 'No products available'}
                      </h3>
                      <p className="text-sm text-slate-500 mb-6 max-w-md mx-auto px-4">
                        {searchActive ? 'Try adjusting your search terms or browse categories.' : 'This category is currently empty.'}
                      </p>
                      <button
                        onClick={handleBackToHome}
                        className="px-6 py-2.5 sm:py-3 bg-[#06402B] text-white rounded-xl hover:bg-emerald-800 transition-all font-semibold text-sm sm:text-base shadow-lg active:scale-95"
                      >
                        Browse All Products
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 md:gap-6">
                        {visibleProducts.map((p) => (
                          <ProductCard 
                            key={p.asin}
                            product={p}
                            isInWishlist={wishlist.includes(p.asin)}
                            onWishlistToggle={(e) => handleWishlistToggle(p.asin, e)}
                            onProductClick={() => handleProductClick(p)}
                          />
                        ))}
                      </div>

                      {hasMore && (
                        <div className="flex justify-center mt-8 sm:mt-12">
                          <button 
                            onClick={loadMore} 
                            disabled={loadingMore} 
                            className="w-full sm:w-auto px-6 sm:px-8 py-3.5 sm:py-4 bg-[#06402B] text-white rounded-xl sm:rounded-2xl hover:bg-[#053527] font-semibold shadow-lg shadow-emerald-900/10 flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base active:scale-95 transition-all"
                          >
                            {loadingMore ? (
                              <><div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Loading...</>
                            ) : (
                              <>Load More<ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></>
                            )}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>
      
      <MainFooter />
      <LoginModal />
      <MobileFilterDrawer />

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .pb-safe { padding-bottom: env(safe-area-inset-bottom); }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-in { animation-fill-mode: both; }
        .fade-in { animation: fade-in 0.3s ease-out; }
        .slide-in-from-bottom-10 { animation: slideUp 0.3s ease-out; }
        @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
      `}</style>
    </div>
  );
};

export default HomeScreen;